/**
 * #14 燕窩存摺 API client
 *
 * 1 組 = 6 瓶 (對齊後端 wallet_service.BOTTLES_PER_SET);
 * 「組」是顯示單位,後端只存「瓶」, balance_sets/balance_remainder 由 backend derive。
 */
import { api } from "./client";

// 品類:即飲(瓶) / 鮮燉(瓶,140克) / 留白禮盒(組) — 各記各的,單位不互換
// + 內部送禮包材(internal=true):禮盒(個) / 精美紙袋(個) — 不對客販售,
//   純管理者手動入帳(例:買 100 瓶送 2 禮盒 10 紙袋);會員端沒拿過不顯示
export interface WalletCategory {
  category: string;   // drink / fresh / giftbox / box / bag
  name: string;       // 即飲 / 鮮燉 / 留白禮盒 / 禮盒 / 精美紙袋
  unit: string;       // 瓶 / 組 / 個
  spec: string;       // 鮮燉="140 克"、包材="送禮包材",其餘 ""
  internal?: boolean; // true = 送禮包材(禮盒/紙袋),會員端有往來才顯示
  balance: number;
  credited: number;
  debited: number;
}

export interface WalletBalance {
  line_user_id: string;
  categories: WalletCategory[];   // 三品類(主要顯示)
  // 舊制單一總瓶 — 訂單自動路徑寫的「既有餘額」,>0 才另列一行;新功能不動它
  balance_bottles: number;
  balance_sets: number;
  balance_remainder: number;
  bottles_per_set: number;
  total_credited: number;
  total_debited: number;
  exists: boolean;
}

// 會員端 /api/wallet/transactions 只回白名單欄位 — 後端 wallet_service.PUBLIC_TXN_FIELDS。
// ref_type / ref_id / operator 只有 /api/admin/wallet/<uid>/transactions 會回(後台頁共用這個 type)。
export interface WalletTxn {
  txn_id: string;
  type: "credit" | "debit";
  // 新制品類交易:category + unit + qty;舊制交易:bottles(單位一律瓶)
  category?: string;
  unit?: string;
  qty?: number;
  bottles?: number;
  balance_after: number;
  reason: string;
  ref_type: string;
  ref_id: string;
  note: string;
  operator: string;
  created_at?: string;
}

// 對齊後端 reason 字典(reason → 中文 label,顯示用)
export const WALLET_REASON_LABELS: Record<string, string> = {
  order_paid: "訂單入帳",
  // 會員存摺明細看得到(後台會員頁也共用):不用「客服」,改「顧問」(2026-10-12 品牌用語)
  admin_credit: "顧問入帳",
  admin_debit: "顧問出帳",
  gift_sent: "送禮出帳",
  gift_received: "收禮入帳",
  referral_referrer: "推薦獎勵",
  referral_referee: "新客推薦禮",
  merge_from_manual: "帳號合併入帳",
  merge_to_line: "帳號合併轉出",
  timeless_enroll: "恆時薈入會配額",
  offline_delivery: "線下交付核銷",
  order_edit_adjust: "改單調整",
  order_reversal: "取消訂單沖銷",
  shipment: "出貨提領",
  shipment_rollback: "出貨失敗回滾",
};

// 品類顏色(會員端 / 後台共用)— 即飲鼠尾草綠、鮮燉暖棕、留白禮盒珊瑚、燕盞深墨;包材中性灰
export const WALLET_CATEGORY_ACCENT: Record<string, string> = {
  drink: "text-primary",
  fresh: "text-amber-700",
  giftbox: "text-coral",
  dry: "text-ink",
  box: "text-muted",
  bag: "text-muted",
};

// 品類 code → 中文名(交易明細顯示用;餘額卡直接用後端帶的 name)
export const WALLET_CATEGORY_NAMES: Record<string, string> = {
  drink: "即飲",
  fresh: "鮮燉",
  giftbox: "留白禮盒",
  dry: "燕盞",
  box: "禮盒",
  bag: "精美紙袋",
};

// 品類 code → 單位(恆時薈方案 / 後台顯示用;即飲鮮燉=瓶、留白禮盒=組、燕盞=盒、包材=個)
// ⚠️ 要跟後端 wallet_service.WALLET_CATEGORIES 對齊,那邊加品類這裡要一起加
export const WALLET_CATEGORY_UNITS: Record<string, string> = {
  drink: "瓶",
  fresh: "瓶",
  giftbox: "組",
  dry: "盒",
  box: "個",
  bag: "個",
};


// ── 會員端 ───────────────────────────────

export async function getMyWallet(): Promise<WalletBalance> {
  const { data } = await api.get("/api/wallet");
  return data;
}

export async function getMyWalletTransactions(limit: number = 50): Promise<WalletTxn[]> {
  const { data } = await api.get("/api/wallet/transactions", { params: { limit } });
  return data.transactions;
}


// ── 後台 ────────────────────────────────

export async function adminGetWallet(uid: string): Promise<WalletBalance> {
  const { data } = await api.get(`/api/admin/wallet/${uid}`);
  return data;
}

export async function adminGetWalletTransactions(uid: string, limit: number = 100): Promise<WalletTxn[]> {
  const { data } = await api.get(`/api/admin/wallet/${uid}/transactions`, { params: { limit } });
  return data.transactions;
}

export async function adminWalletCredit(uid: string, category: string, qty: number, note: string = "") {
  const { data } = await api.post(`/api/admin/wallet/${uid}/credit`, { category, qty, note });
  return data as { status: string; balance_after?: number; txn_id?: string; category?: string };
}

export async function adminWalletDebit(uid: string, category: string, qty: number, note: string = "") {
  const { data } = await api.post(`/api/admin/wallet/${uid}/debit`, { category, qty, note });
  return data as { status: string; balance_after?: number; txn_id?: string; balance?: number; needed?: number; category?: string };
}

// 硬刪一筆交易 + 餘額自動回正(危險,不可復原)。would_go_negative → 該筆已被花掉部分,改用出帳
export async function adminWalletDeleteTxn(uid: string, txnId: string) {
  const { data } = await api.delete(`/api/admin/wallet/${uid}/transactions/${txnId}`);
  // 舊制交易回 bottles、新制品類交易回 qty + category
  return data as { status: string; balance_after?: number; type?: string; bottles?: number; qty?: number; category?: string };
}
